import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  SupernaturalContextValue,
  type DistortionType,
  type SupernaturalApi,
} from './useSupernatural';

/*
 * SupernaturalProvider.tsx — 全画面「気配（超常）」演出の状態保持（P2「端末は付喪神」基盤・2026-06-21）
 *
 * 役割:
 *  - 継続気配（ambient）と一過性気配（pulse / connection）を別々に保持し、最終 intensity に合成する。
 *  - prefers-reduced-motion を matchMedia で監視し、最終 intensity を弱める（視覚効果の最小化）。
 *  - <SupernaturalOverlay/> が購読する描画状態（active / intensity / type / connectionLabel）を供給する。
 *
 * 既存ファイル非変更方針のため、本 Provider をどこにマウントするか・気配を “いつ” 出すかは後続 Wave が決める。
 * 本 Wave は API と合成・degradation のみを提供する。
 *
 * SSR/window 不在で throw しない（matchMedia は typeof window ガード）。
 */

/** reduced-motion 時に最終 intensity へ掛ける係数（気配は残すが大幅に弱める）。 */
const REDUCED_MOTION_MULTIPLIER = 0.3;

/** 各 API の既定値（世界観: 控えめ）。 */
const DEFAULT_PULSE_INTENSITY = 0.5;
const DEFAULT_PULSE_DURATION_MS = 700;
const DEFAULT_PULSE_TYPE: DistortionType = 'noise';

const DEFAULT_CONNECTION_INTENSITY = 0.6;
const DEFAULT_CONNECTION_DURATION_MS = 1500;
const DEFAULT_CONNECTION_LABEL = '……接続';

const DEFAULT_AMBIENT_TYPE: DistortionType = 'noise';

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/** 0..1 を 0 で停止/それ以外で点灯と扱う共通判定。 */
function isOn(intensity: number): boolean {
  return intensity > 0;
}

export interface SupernaturalProviderProps {
  children: ReactNode;
}

/**
 * Overlay が購読する描画状態（公開 SupernaturalApi に加えて render 専用フィールドを含む）。
 * 公開 hook（useSupernatural）の型は意図的に狭く保ち、Overlay だけがこの拡張形を読む。
 */
export interface SupernaturalRenderState extends SupernaturalApi {
  /** 現在適用する歪みの種別（transient 優先・なければ ambient）。 */
  type: DistortionType;
  /** connection シーケンス表示中のラベル（非表示時は null）。 */
  connectionLabel: string | null;
}

/** ambient の保持形。 */
interface AmbientState {
  intensity: number;
  type: DistortionType;
}

/** transient（pulse / connection）の保持形。 */
interface TransientState {
  intensity: number;
  type: DistortionType;
  /** connection の文字化けラベル（pulse の場合は null）。 */
  label: string | null;
}

const NO_TRANSIENT: TransientState = { intensity: 0, type: DEFAULT_PULSE_TYPE, label: null };

export function SupernaturalProvider({ children }: SupernaturalProviderProps): React.JSX.Element {
  const [ambient, setAmbient] = useState<AmbientState>({ intensity: 0, type: DEFAULT_AMBIENT_TYPE });
  const [transient, setTransient] = useState<TransientState>(NO_TRANSIENT);

  // reduced-motion フラグ（SSR 安全に初期化し、変化を監視）。
  const [reducedMotion, setReducedMotion] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent): void => setReducedMotion(e.matches);
    // 念のため購読開始時点の最新値で同期。
    setReducedMotion(mql.matches);
    // addEventListener は新しめのブラウザ。古い実装の addListener へはフォールバックしない
    // （対象ブラウザは MediaQueryList.addEventListener を備える前提・型安全のため）。
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // 一過性タイマーの ref（多重発火時は最新で上書きし、unmount で必ず掃除）。
  const transientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTransientTimer = useCallback(() => {
    if (transientTimerRef.current !== null) {
      clearTimeout(transientTimerRef.current);
      transientTimerRef.current = null;
    }
  }, []);
  useEffect(() => () => clearTransientTimer(), [clearTransientTimer]);

  // ---- API 実装 ----
  const setAmbientDistortion = useCallback((intensity: number, type: DistortionType = DEFAULT_AMBIENT_TYPE) => {
    setAmbient({ intensity: clamp01(intensity), type });
  }, []);

  const pulse = useCallback(
    (opts?: { intensity?: number; durationMs?: number; type?: DistortionType }) => {
      const intensity = clamp01(opts?.intensity ?? DEFAULT_PULSE_INTENSITY);
      const durationMs = Math.max(0, opts?.durationMs ?? DEFAULT_PULSE_DURATION_MS);
      const type = opts?.type ?? DEFAULT_PULSE_TYPE;
      clearTransientTimer();
      setTransient({ intensity, type, label: null });
      transientTimerRef.current = setTimeout(() => {
        setTransient(NO_TRANSIENT);
        transientTimerRef.current = null;
      }, durationMs);
    },
    [clearTransientTimer],
  );

  const playConnectionSequence = useCallback(
    (opts?: { intensity?: number; durationMs?: number; label?: string }): number => {
      const intensity = clamp01(opts?.intensity ?? DEFAULT_CONNECTION_INTENSITY);
      const durationMs = Math.max(0, opts?.durationMs ?? DEFAULT_CONNECTION_DURATION_MS);
      const label = opts?.label ?? DEFAULT_CONNECTION_LABEL;
      clearTransientTimer();
      // connection は質感を 'tear'（裂け目＝接続が割り込む）に固定し、ラベルを伴って表示。
      setTransient({ intensity, type: 'tear', label });
      transientTimerRef.current = setTimeout(() => {
        setTransient(NO_TRANSIENT);
        transientTimerRef.current = null;
      }, durationMs);
      // 後続が phase 切替の待ちに使えるよう、実際に適用した duration を返す。
      return durationMs;
    },
    [clearTransientTimer],
  );

  // ---- 合成（transient 優先・最終 intensity に reduced-motion 補正） ----
  const transientOn = isOn(transient.intensity);
  const rawIntensity = transientOn ? Math.max(transient.intensity, ambient.intensity) : ambient.intensity;
  const finalIntensity = clamp01(reducedMotion ? rawIntensity * REDUCED_MOTION_MULTIPLIER : rawIntensity);
  const activeType: DistortionType = transientOn ? transient.type : ambient.type;
  const connectionLabel = transientOn ? transient.label : null;
  const active = isOn(finalIntensity);

  const value = useMemo<SupernaturalRenderState>(
    () => ({
      setAmbientDistortion,
      pulse,
      playConnectionSequence,
      active,
      intensity: finalIntensity,
      type: activeType,
      connectionLabel,
    }),
    [setAmbientDistortion, pulse, playConnectionSequence, active, finalIntensity, activeType, connectionLabel],
  );

  return <SupernaturalContextValue.Provider value={value}>{children}</SupernaturalContextValue.Provider>;
}

export default SupernaturalProvider;
